/**
 * @file ConversationList.tsx
 * @description Список диалогов окна «Сообщения» (chat-window-design.md §2,
 * §5, §6): шапка с вкладками и отбором, строки, состояния, «Показать ещё»,
 * подсветка новых диалогов и пилюля «↑ Новые диалоги: N», если список
 * прокручен (каждый диалог считается в пилюле один раз). Клавиши: ↑ / ↓ —
 * между строками, Enter — открыть. Над списком — спокойное сообщение окна
 * (`notice`, например «диалога ещё нет» под сотрудником). Вид строк
 * считается только при новых данных: ввод в поиске строки не перерисовывает.
 */

import { ArrowUp } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState, type KeyboardEvent } from "react";

import { Button } from "../ui/button";
import { ListError } from "../ui/ListStates";
import { NoticeStrip } from "../ui/NoticeStrip";
import { Skeleton } from "../ui/skeleton";
import { conversationRowView } from "./chat-row-view";
import { CHAT_TEXT } from "./chat-texts";
import { useChatWindow } from "./chat-window-context";
import { ConversationListEmpty } from "./ConversationListEmpty";
import { ConversationListToolbar } from "./ConversationListToolbar";
import { ConversationRow } from "./ConversationRow";
import { listEmptyKind } from "./list-empty-kind";
import type { ConversationListData } from "./useConversationList";

interface Props {
  list: ConversationListData;
}

function RowsSkeleton() {
  return (
    <div aria-busy="true" aria-label="Загрузка диалогов" className="flex flex-col">
      {Array.from({ length: 6 }, (_, i) => (
        <div key={i} className="flex min-h-[72px] flex-col justify-center gap-2 px-4">
          <Skeleton className="h-4 w-1/2" />
          <Skeleton className="h-3 w-4/5" />
        </div>
      ))}
    </div>
  );
}

/** ↑ / ↓ между строками списка. */
function moveFocus(event: KeyboardEvent<HTMLButtonElement>) {
  if (event.key !== "ArrowDown" && event.key !== "ArrowUp") return;
  const rows = [...(event.currentTarget.closest("[data-chat-list]")?.querySelectorAll<HTMLButtonElement>("[data-chat-row]") ?? [])];
  const index = rows.indexOf(event.currentTarget);
  const next = rows[event.key === "ArrowDown" ? index + 1 : index - 1];
  if (next) {
    event.preventDefault();
    next.focus();
  }
}

export function ConversationList({ list }: Props) {
  const chat = useChatWindow();
  const { state } = chat;
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const [scrolled, setScrolled] = useState(false);
  const [freshAbove, setFreshAbove] = useState(0);
  /** Диалоги, уже посчитанные в пилюле: прокрутка туда-обратно не считает их снова. */
  const countedFresh = useRef<Set<string>>(new Set());

  // Вид строк — только при новых данных: ввод в поиске строки не перерисовывает.
  const views = useMemo(() => {
    const now = new Date();
    return list.items.map((c) => ({ id: c.id, view: conversationRowView(c, chat.userId, now) }));
  }, [list.items, chat.userId]);

  // Один обработчик выбора на все строки (стабильный пропс для `memo`).
  const selectRef = useRef(chat.select);
  selectRef.current = chat.select;
  const onSelect = useCallback((id: string) => selectRef.current(id), []);

  // Новые диалоги при прокрученном списке — пилюля вместо прыжка.
  useEffect(() => {
    if (!scrolled || list.freshIds.size === 0) return;
    const added = [...list.freshIds].filter((id) => !countedFresh.current.has(id));
    if (added.length === 0) return;
    added.forEach((id) => countedFresh.current.add(id));
    setFreshAbove((n) => n + added.length);
  }, [list.freshIds, scrolled]);

  // Имя клиента в отборе, открытом из карточки без имени.
  const first = list.items[0];
  useEffect(() => {
    if (state.customer?.name === "…" && first && first.customerId === state.customer.id) {
      chat.setCustomer({ id: state.customer.id, name: first.customer.displayName ?? first.customer.email ?? "Клиент" });
    }
  }, [state.customer, first, chat]);

  const toTop = () => {
    const el = scrollRef.current;
    if (el && typeof el.scrollTo === "function") el.scrollTo({ top: 0, behavior: "smooth" });
    setFreshAbove(0);
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <ConversationListToolbar
        canDispatch={chat.canDispatch}
        tab={chat.canDispatch ? state.tab : "mine"}
        unassigned={state.unassigned}
        customer={state.customer}
        search={state.search}
        counts={list.counts}
        onTab={chat.setTab}
        onUnassigned={chat.setUnassigned}
        onCustomer={chat.setCustomer}
        onSearch={chat.setSearch}
      />
      {chat.notice ? (
        <NoticeStrip
          tone="info"
          className="m-2"
          action={
            <Button variant="ghost" size="sm" className="min-h-12 lg:min-h-8" onClick={chat.dismissNotice}>
              {CHAT_TEXT.understood}
            </Button>
          }
        >
          {chat.notice}
        </NoticeStrip>
      ) : null}
      {list.stale ? <NoticeStrip tone="warning" className="m-2">{CHAT_TEXT.listStale}</NoticeStrip> : null}
      <div className="relative flex min-h-0 flex-1 flex-col">
        {freshAbove > 0 ? (
          <Button variant="soft" size="sm" className="absolute left-1/2 top-2 z-10 min-h-10 -translate-x-1/2 rounded-full shadow-sm" onClick={toTop}>
            <ArrowUp className="size-4" aria-hidden />
            {CHAT_TEXT.newConversations(freshAbove)}
          </Button>
        ) : null}
        <div
          ref={scrollRef}
          data-chat-list=""
          className="min-h-0 flex-1 overflow-y-auto overscroll-contain"
          onScroll={(e) => {
            const top = e.currentTarget.scrollTop > 8;
            setScrolled(top);
            if (!top) setFreshAbove(0);
          }}
        >
          {list.loading ? (
            <RowsSkeleton />
          ) : list.error ? (
            <div className="p-3">
              <ListError message={CHAT_TEXT.listError} onRetry={list.reload} retryLabel={CHAT_TEXT.retry} />
            </div>
          ) : list.items.length === 0 ? (
            <ConversationListEmpty
              kind={listEmptyKind(chat.canDispatch ? state.tab : "mine", state.unassigned, state.search)}
              search={state.search}
              canDispatch={chat.canDispatch}
              onOpenAll={() => chat.setTab("all")}
              onResetUnassigned={() => chat.setUnassigned(false)}
            />
          ) : (
            <ul className="flex flex-col divide-y divide-outline-variant/60">
              {views.map(({ id, view }) => (
                <li key={id}>
                  <ConversationRow
                    id={id}
                    view={view}
                    selected={id === state.selectedId}
                    fresh={list.freshIds.has(id)}
                    onSelect={onSelect}
                    onKeyDown={moveFocus}
                  />
                </li>
              ))}
            </ul>
          )}
          {list.hasMore && !list.loading ? (
            <div className="p-3">
              <Button variant="outline" className="min-h-12 w-full lg:min-h-10" loading={list.loadingMore} onClick={list.loadMore}>
                {CHAT_TEXT.showMore}
              </Button>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
